import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Lock } from '../../lib/icons';
import { useAuth } from '../../lib/auth';
import { fmt } from '../shared/badges';
import { corrigerRepriseCca, creerRepriseCca, fetchReprisesCca, validerRepriseCca } from '../../lib/cca';

const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white';

// Écran administrateur permanent : reprise des soldes initiaux CCA.
// Aucun mouvement de trésorerie, aucun produit, aucune charge.
export const SoldesInitiauxCCA = ({ comptes, onDone }) => {
  const { user } = useAuth();
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [reprises, setReprises] = useState([]);
  const [f, setF] = useState({ contact_id: '', montant: '', date_reprise: aujourdhui, commentaire: '', deja_comptabilise: false });
  const [brouillon, setBrouillon] = useState(null);

  const load = useCallback(() => {
    fetchReprisesCca(user).then(setReprises);
  }, [user]);
  useEffect(() => {
    load();
  }, [load]);

  const associe = comptes.find((c) => c.contact_id === f.contact_id);

  const preparer = async (e) => {
    e.preventDefault();
    if (!f.contact_id) return toast.error('Choisissez l’associé.');
    if (f.montant === '') return toast.error('Saisissez le montant du solde initial.');
    const { error, data } = await creerRepriseCca({ ...f, montant: Number(f.montant) }, user);
    if (error) return toast.error(error);
    setBrouillon(data);
    load();
  };

  const valider = async () => {
    const { error, data } = await validerRepriseCca(brouillon.id, user);
    if (error) return toast.error(error);
    toast.success(`Solde initial de ${fmt(data.montant, 3)} TND repris au ${data.date_reprise} — aucun mouvement de caisse ni de banque créé.`);
    setBrouillon(null);
    setF({ contact_id: '', montant: '', date_reprise: aujourdhui, commentaire: '', deja_comptabilise: false });
    load();
    onDone?.();
  };

  const corriger = async (r) => {
    const montant = window.prompt(`Nouveau solde initial (TND) pour ${r.contact_id} :`, String(r.montant));
    if (montant === null) return;
    const motif = window.prompt('Motif de la correction (obligatoire) :');
    if (!motif || motif.trim().length < 3) return toast.error('Motif obligatoire.');
    const { error } = await corrigerRepriseCca(r.id, { montant: Number(montant), motif: motif.trim() }, user);
    if (error) return toast.error(error);
    toast.success('Correction enregistrée — l’historique des valeurs est conservé.');
    load();
    onDone?.();
  };

  return (
    <section data-testid="cca-reprise" className="border-2 border-slate-900 bg-white">
      <div className="border-b border-slate-200 bg-slate-900 px-4 py-3">
        <p className="font-heading text-sm font-bold uppercase tracking-[0.08em] text-white">Soldes initiaux CCA — Administrateur</p>
        <p className="mt-0.5 text-[11px] text-slate-300">
          Reprise d’une dette DÉJÀ existante : aucun mouvement de caisse ou de banque, aucun produit ni charge. Une
          seconde reprise cumulée est impossible ; toute correction reste tracée.
        </p>
      </div>

      <form onSubmit={preparer} className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-4">
        <div>
          <label className={label}>Associé</label>
          <select data-testid="reprise-associe" value={f.contact_id} onChange={(e) => setF({ ...f, contact_id: e.target.value })} className={field}>
            <option value="">— Choisir —</option>
            {comptes.map((c) => (
              <option key={c.contact_id} value={c.contact_id}>{c.nom}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={label}>Solde initial (TND)</label>
          <input data-testid="reprise-montant" type="number" step="0.001" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} className={field} />
        </div>
        <div>
          <label className={label}>Date de reprise</label>
          <input data-testid="reprise-date" type="date" value={f.date_reprise} onChange={(e) => setF({ ...f, date_reprise: e.target.value })} className={field} />
        </div>
        <div>
          <label className={label}>Commentaire / justificatif</label>
          <input data-testid="reprise-commentaire" value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} className={field} placeholder="PV d’assemblée, balance comptable…" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-700 sm:col-span-3">
          <input data-testid="reprise-deja-comptabilise" type="checkbox" checked={f.deja_comptabilise} onChange={(e) => setF({ ...f, deja_comptabilise: e.target.checked })} />
          Ce solde existe déjà en comptabilité : rattachement sans seconde comptabilisation.
        </label>
        <button type="submit" data-testid="reprise-preparer" className="h-11 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white">
          Préparer le brouillon
        </button>
      </form>

      {brouillon && (
        <div data-testid="reprise-recap" className="mx-4 mb-4 border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900">
          <p className="font-bold uppercase tracking-[0.08em]">Récapitulatif avant validation</p>
          <ul className="mt-1 space-y-0.5 font-data">
            <li>Associé : {associe?.nom ?? brouillon.contact_id}</li>
            <li>Solde initial : {fmt(brouillon.montant, 3)} TND (dette de l’entreprise envers l’associé si positif)</li>
            <li>Date de reprise : {brouillon.date_reprise}</li>
            <li>Commentaire : {brouillon.commentaire || '—'}</li>
            <li>Déjà comptabilisé : {brouillon.deja_comptabilise ? 'oui — rattachement sans nouvelle écriture' : 'non'}</li>
            <li>Effet trésorerie : aucun</li>
          </ul>
          {brouillon.chevauchements?.length > 0 && (
            <p data-testid="reprise-chevauchement" className="mt-2 flex items-start gap-1 font-semibold">
              <AlertTriangle size={13} className="mt-0.5" /> {brouillon.chevauchements.length} mouvement(s) CCA
              antérieur(s) ou du même jour ({brouillon.chevauchements.join(', ')}) : ils seront conservés mais exclus du
              recalcul pour éviter un double comptage.
            </p>
          )}
          <div className="mt-3 flex gap-2">
            <button type="button" data-testid="reprise-valider" onClick={valider} className="h-10 bg-slate-900 px-4 text-xs font-bold uppercase tracking-[0.08em] text-white">
              Valider la reprise
            </button>
            <button type="button" data-testid="reprise-annuler" onClick={() => setBrouillon(null)} className="h-10 border border-amber-400 px-4 text-xs font-semibold">
              Annuler le brouillon
            </button>
          </div>
        </div>
      )}

      {reprises.length > 0 && (
        <table className="w-full border-collapse text-left" data-testid="reprise-table">
          <thead>
            <tr className="border-y border-slate-200 bg-slate-50">
              {['Associé', 'Montant', 'Date de reprise', 'Statut', 'Commentaire', 'Corrections', ''].map((c, i) => (
                <th key={c || i} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reprises.map((r) => (
              <tr key={r.id} data-testid={`reprise-row-${r.id}`} className="border-b border-slate-100">
                <td className="px-3 py-2 text-sm">{comptes.find((c) => c.contact_id === r.contact_id)?.nom ?? r.contact_id}</td>
                <td className="tabular px-3 py-2 font-data text-sm font-semibold">{fmt(r.montant, 3)} TND</td>
                <td className="px-3 py-2 font-data text-sm">{r.date_reprise}</td>
                <td className="px-3 py-2">
                  <span className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${r.statut === 'valide' ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                    {r.statut === 'valide' ? <Lock size={10} className="mr-1 inline" /> : null}
                    {r.statut}
                  </span>
                </td>
                <td className="px-3 py-2 text-[11px] text-slate-600">{r.commentaire || '—'}</td>
                <td className="px-3 py-2 text-[11px] text-slate-600">{(r.corrections ?? []).length}</td>
                <td className="px-3 py-2 text-right">
                  <button type="button" data-testid={`reprise-corriger-${r.id}`} onClick={() => corriger(r)} className="border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-900 hover:text-white">
                    Corriger
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};
